import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import {
  Module,
  ModuleBody,
  ModuleFooter,
  ModuleHeader,
  Wrapper,
  Button,
} from "@wfp/ui";

import Table from "components/CountryAdmin/CountryAdminContent/Table/Table";
import Filtering from "components/CountryAdmin/CountryAdminContent/Table/Filtering";
import CsvImportModal from "../CsvImportModal";

import {
  getVendorsImports,
  requestVendorsImports,
} from "data-handler/ducks/vendors";

import "../AllVendors/_content.scss";

const VendorsImports = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const vendorsImports = useSelector(getVendorsImports);

  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filtersState, setFilters] = useState([]);
  const [csvImportModalOpen, setCsvImportModalOpen] = useState(false);

  const { count = 0, ids = [] } = vendorsImports;
  const data = ids.map((id) => vendorsImports.results[id]);

  const statusOptions = [
    {
      name: "",
      label: intl.formatMessage({
        id: "countryAdmin.allImports",
        defaultMessage: "All imports",
      }),
    },
    {
      name: 1,
      label: intl.formatMessage({
        id: "countryAdmin.pending",
        defaultMessage: "Pending",
      }),
    },
    {
      name: 2,
      label: intl.formatMessage({
        id: "countryAdmin.running",
        defaultMessage: "Running",
      }),
    },
    {
      name: 3,
      label: intl.formatMessage({
        id: "countryAdmin.completed",
        defaultMessage: "Completed",
      }),
    },
  ];

  const filterProps = {
    fields: [
      {
        name: "csv_file",
        label: intl.formatMessage({
          id: "countryAdmin.csvFile",
          defaultMessage: "CSV File",
        }),
      },
      {
        name: "created_by_email",
        label: intl.formatMessage({
          id: "countryAdmin.createdBy",
          defaultMessage: "Created by",
        }),
      },
      {
        name: "status",
        label: intl.formatMessage({
          id: "countryAdmin.status",
          defaultMessage: "Status",
        }),
        type: "select",
        options: statusOptions,
      },
      {
        name: "country_name",
        label: intl.formatMessage({
          id: "CountryAdmin.country",
          defaultMessage: "Country",
        }),
      },
    ],
    onSubmit: (e) => {
      const filters = Object.keys(e).map((key) => ({ id: key, value: e[key] }));
      setFilters(filters);
      loadPage(pageSize, 1, "", filters);
    },
    defaults: filtersState.reduce((a, x) => ({ ...a, [x.id]: x.value }), {}),
  };

  const loadPage = (size, idx, ordering, filters = filtersState) => {
    setPageSize(size);
    setPageNum(idx);
    dispatch(requestVendorsImports(size, idx, ordering, filters));
  };

  const statusCell = ({ row }) => {
    const status = row["original"]["status"];
    const statusOption = statusOptions.find((item) => item.name === status);
    return statusOption ? statusOption.label : "";
  };

  const csvFilenameCell = ({ row }) => {
    const importId = row["original"]["id"];
    const filename = row["original"]["csv_filename"];
    return (
      <Link to={`/countryAdmin/vendors/vendorsImports/${importId}`}>
        {filename}
      </Link>
    );
  };

  const columns = useMemo(() => {
    return [
      {
        Header: intl.formatMessage({
          id: "countryAdmin.csvFilename",
          defaultMessage: "CSV filename",
        }),
        accessor: "csv_filename",
        Cell: csvFilenameCell,
      },
      {
        Header: intl.formatMessage({
          id: "countryAdmin.createdBy",
          defaultMessage: "Created by",
        }),
        accessor: "created_by.email",
      },
      {
        Header: intl.formatMessage({
          id: "countryAdmin.status",
          defaultMessage: "Status",
        }),
        accessor: "status",
        Cell: statusCell,
      },
      {
        Header: intl.formatMessage({
          id: "countryAdmin.created",
          defaultMessage: "Created",
        }),
        accessor: "num_created",
        disableSortBy: true,
      },
      {
        Header: intl.formatMessage({
          id: "countryAdmin.updated",
          defaultMessage: "Updated",
        }),
        accessor: "num_updated",
        disableSortBy: true,
      },
      {
        Header: intl.formatMessage({
          id: "countryAdmin.failed",
          defaultMessage: "Failed",
        }),
        accessor: "num_failed",
        disableSortBy: true,
      },
      {
        Header: intl.formatMessage({
          id: "countryAdmin.total",
          defaultMessage: "Total",
        }),
        accessor: "total",
        disableSortBy: true,
      },
      {
        Header: intl.formatMessage({
          id: "CountryAdmin.country",
          defaultMessage: "Country",
        }),
        accessor: "country.name",
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableProps = {
    columns,
    data,
    pageNum,
    pageSize,
    totalItems: count,
    loadPage,
  };

  const csvImportModalProps = {
    csvImportModalOpen,
    setCsvImportModalOpen,
  };

  return (
    <Wrapper pageWidth="lg" spacing="md" background="lighter" className="table">
      <Module>
        <ModuleHeader>
          {intl.formatMessage({
            id: "countryAdmin.vendorsImports",
            defaultMessage: "Vendors Imports",
          })}
        </ModuleHeader>
        <ModuleBody>
          <Filtering {...filterProps} />
          <Table {...tableProps} showSort />
          <CsvImportModal {...csvImportModalProps} />
        </ModuleBody>
        <ModuleFooter>
          <div className="vendors-action-buttons">
            <Button
              className="wfp--btn--secondary"
              onClick={() => {
                setCsvImportModalOpen(true);
              }}
            >
              <FormattedMessage
                id="modal.importCsv"
                defaultMessage="Import CSV"
              />
            </Button>
          </div>
        </ModuleFooter>
      </Module>
    </Wrapper>
  );
};

export default VendorsImports;
